// utils/dataLoader.ts
import { CountryData, CountryBasicInfo } from '../types';
import { message } from 'antd';

// Définition des coordonnées centrales et limites pour chaque pays
const countryCoordinates: Record<string, { 
  center: [number, number], 
  bounds: [[number, number], [number, number]],
  color: string
}> = {
  'FR': {
    center: [2.2137, 46.2276],
    bounds: [[-5.4, 41.3], [9.6, 51.1]],
    color: '#15b89a'
  },
  'US': {
    center: [-95.7129, 37.0902],
    bounds: [[-125.0, 24.4], [-66.9, 49.4]],
    color: '#15b89a'
  },
  'BR': {
    center: [-51.305951, -19.952955],
    bounds: [[-73.9, -33.7], [-34.8, 5.2]],
    color: '#15b89a'
  },
  'DE': {
    center: [10.4515, 51.1657],
    bounds: [[5.9, 47.3], [15.0, 55.1]],
    color: '#15b89a'
  },
  'UK': {
    center: [-3.4360, 55.3781],
    bounds: [[-8.6, 49.9], [1.7, 60.8]],
    color: '#15b89a'
  },
  // Ajoutez d'autres pays au besoin
};

// Fonction pour obtenir la liste des pays depuis le dossier data avec leurs coordonnées
export const fetchCountryList = async (): Promise<CountryBasicInfo[]> => {
  try {
    // Dans un contexte webpack/vite
    // Cette approche fonctionnera avec Vite
    const modules = import.meta.glob('/public/data/*.json');
    const countryFiles = Object.keys(modules);
    
    const countries = countryFiles.map(file => {
      // Extraire le code du pays du nom de fichier (ex: "fr.json" -> "FR")
      const match = file.match(/\/data\/([a-z]{2})\.json$/i);
      if (!match) return null;
      
      const countryCode = match[1].toUpperCase();
      
      // Mapper le code pays à un nom complet
      const countryNames: Record<string, string> = {
        'FR': 'France',
        'US': 'United States',
        'BR': 'Brazil',
        'DE': 'Germany',
        'UK': 'United Kingdom',
      };
      
      const countryName = countryNames[countryCode] || countryCode;
      
      // Obtenir les coordonnées du pays
      const coords = countryCoordinates[countryCode];
      
      if (!coords) {
        // Coordonnées par défaut si aucune n'est définie pour ce pays
        return {
          name: countryName,
          center: [0, 0],
          bounds: [[-10, -10], [10, 10]],
          color: '#999999'
        };
      }
      
      // Retourner le pays avec ses coordonnées
      return {
        name: countryName,
        center: coords.center,
        bounds: coords.bounds,
        color: coords.color,
        id: countryCode
      };
    }).filter(Boolean) as CountryBasicInfo[];
    
    return countries.sort((a, b) => a.name.localeCompare(b.name)); // Tri alphabétique

  } catch (error) {
    console.error("Erreur lors du chargement de la liste des pays:", error);
    message.error("Impossible de charger la liste des pays");
    return [];
  }
};

// Function to load country data from a JSON file
export const fetchCountryData = async (countryName: string): Promise<{ countryData: CountryData, fullGeoJSON: any }> => {
  try {
    // Map country name to country code
    const countryMapping: Record<string, string> = {
      'France': 'fr',
      'United States': 'us',
      'Brazil': 'br',
      'Germany': 'de',
      'United kingdom': 'uk'
    };

    const countryCode = countryMapping[countryName]?.toLowerCase();

    if (!countryCode) {
      throw new Error(`Country code not found for ${countryName}`);
    }

    const response = await fetch(`/data/${countryCode}.json`);

    if (!response.ok) {
      throw new Error(`HTTP Error: ${response.status}`);
    }

    const jsonData = await response.json();

    // Transform GeoJSON data to CountryData format
    const countryData: CountryData = {
      name: countryName,
      pointsOfInterest: [],
      // Ces valeurs seront écrasées par l'appelant avec les vraies coordonnées
      center: [0, 0],
      bounds: [[0, 0], [0, 0]],
      color: '#000000',
      id: countryCode.toLowerCase()
    };

    // Extract points of interest from GeoJSON data
    if (jsonData.features && Array.isArray(jsonData.features)) {
      // First, find the markers
      const markers = jsonData.features.filter(
        (feature: any) => feature.properties?.type === 'marker'
      );

      // For each marker, add a point of interest
      markers.forEach((marker: any) => {
        if (marker.geometry?.type === 'Point' && Array.isArray(marker.geometry.coordinates)) {
          countryData.pointsOfInterest.push({
            name: marker.properties?.name || 'Point of Interest',
            coordinates: marker.geometry.coordinates as [number, number],
            seasons: marker.properties?.seasons || [],
            id: marker.properties?.id || ''
          });
        }
      });
    }

    // Return both structured data and full GeoJSON
    return {
      countryData,
      fullGeoJSON: jsonData
    };
  } catch (error) {
    console.error(`Error loading data for ${countryName}:`, error);
    // The message will be handled by the component calling this function
    return {
      countryData: {
        name: countryName,
        pointsOfInterest: [],
        // Ces valeurs seront écrasées par l'appelant
        center: [0, 0],
        bounds: [[0, 0], [0, 0]],
        color: '#000000',
        id: ''
      },
      fullGeoJSON: null
    };
  }
};